// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    disabled?: boolean;
    error?: string | null;
};

/**
 * A brand colour, picked or typed.
 *
 * Two controls over one value because the two ways of choosing a colour are not interchangeable: a
 * person deciding reaches for the swatch, and a person who was handed `#1F6F5C` by their designer
 * types it. Taking either away makes one of them guess.
 *
 * The native picker always holds a valid colour, so it writes straight through. The text field can
 * hold anything mid-typing — `#1F6` is three keystrokes from being right — so the swatch keeps
 * showing the last usable value while the field shows exactly what was typed.
 */
export default function ColorInput({ value, onChange, label, disabled, error }: Props) {
    const usable = constants.hexColorPattern.test(value) ? value : constants.defaultBrandColor;

    return (
        <div className="color-input">
            {label && <span className="color-input__label">{label}</span>}

            <div className="color-input__row">
                <input
                    className="color-input__swatch"
                    type="color"
                    value={usable}
                    onChange={(event) => onChange(event.target.value.toUpperCase())}
                    disabled={disabled}
                    aria-label={label}
                />

                <input
                    className={`color-input__field${error ? " color-input__field--error" : ""}`}
                    type="text"
                    value={value}
                    onChange={(event) => onChange(event.target.value.trim())}
                    placeholder={constants.defaultBrandColor}
                    dir="ltr"
                    disabled={disabled}
                    spellCheck={false}
                />
            </div>

            {error && <span className="color-input__error">{error}</span>}
        </div>
    );
}
